import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Text, Tooltip, useLocalizeMessage } from 'libs.nucleus';
/**
 * FieldPicker — composite select-like component that displays
 * the selected field name, type badge, and info icon inline.
 * Matches the design for X axis / Y axis / column selectors.
 */
export const FieldPicker = ({ value, options, onChange, label, required, disabled, placeholder, emptyOptionsMessage, onRemove, }) => {
    const translate = useLocalizeMessage();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) {
            return options;
        }
        return options.filter((option) => option.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm]);
    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);
    // Auto-focus search input and reset search term when dropdown opens/closes
    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => searchInputRef.current?.focus());
        }
        else {
            setSearchTerm('');
        }
    }, [isOpen]);
    const handleToggle = useCallback(() => {
        if (!disabled) {
            setIsOpen((prev) => !prev);
        }
    }, [disabled]);
    const handleSelect = useCallback((field) => {
        onChange(field);
        setIsOpen(false);
    }, [onChange]);
    const handleKeyDown = useCallback((e) => {
        if (e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault();
            handleToggle();
        }
        else if (e.code === 'Escape') {
            setIsOpen(false);
        }
    }, [handleToggle]);
    return (_jsxs("div", { ref: containerRef, className: 'relative w-full', children: [(label || onRemove) && (_jsxs("div", { className: 'flex items-center justify-between mb-2', children: [label && (_jsxs("label", { className: 'font-medium text-sm text-neutral-900', children: [label, required && _jsx("span", { className: 'text-red-500 pl-px', children: "*" })] })), onRemove && (_jsx("button", { type: 'button', onClick: onRemove, className: 'text-neutral-400 hover:text-red-500 p-0.5', "aria-label": 'Remove', children: _jsx(Icon, { name: 'close', color: 'current', size: 'sm' }) }))] })), _jsxs("div", { role: 'combobox', tabIndex: disabled ? -1 : 0, "aria-expanded": isOpen, "aria-haspopup": 'listbox', onClick: handleToggle, onKeyDown: handleKeyDown, className: `flex items-center h-10 w-full min-w-0 border rounded-md px-3 py-2 text-sm ${disabled
                    ? 'bg-neutral-200 border-neutral-200 text-neutral-400 cursor-not-allowed'
                    : isOpen
                        ? 'bg-primary-75 border-primary-75 cursor-pointer'
                        : 'bg-white border-neutral-300 hover:bg-neutral-50 hover:border-neutral-500 cursor-pointer'}`, children: [value ? (_jsxs(_Fragment, { children: [_jsx(Text, { size: 'sm', className: 'truncate flex-1 min-w-0', children: value.name }), _jsxs("div", { className: 'flex items-center gap-1.5 ml-2 flex-shrink-0', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-400 whitespace-nowrap', children: value.type }), _jsx(Tooltip, { title: translate('{type} field', { type: value.type }), placement: 'top', delayShow: 300, children: _jsx("span", { className: 'text-neutral-400', children: _jsx(Icon, { name: 'info', color: 'current' }) }) })] })] })) : (_jsx(Text, { size: 'sm', className: 'text-neutral-400 truncate flex-1 min-w-0', children: placeholder || translate('Select...') })), _jsx("span", { className: 'pl-1 flex-shrink-0', children: _jsx(Icon, { name: isOpen ? 'chevronUp' : 'chevronDown', color: 'gray', size: 'sm' }) })] }), isOpen && (_jsxs("div", { role: 'listbox', className: 'absolute z-50 top-full mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-xl max-h-60 overflow-hidden flex flex-col', children: [_jsx("div", { className: 'p-2 border-b border-neutral-200', children: _jsx("input", { ref: searchInputRef, type: 'text', value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyDown: (e) => {
                                if (e.code === 'Escape') {
                                    setIsOpen(false);
                                }
                            }, placeholder: translate('Search...'), className: 'w-full px-2 py-1.5 text-sm border border-neutral-300 rounded focus:outline-none focus:border-primary-500' }) }), _jsxs("div", { className: 'overflow-y-auto', children: [filteredOptions.map((option) => {
                                const isSelected = value?.key === option.key;
                                return (_jsx("div", { role: 'option', "aria-selected": isSelected, onClick: () => handleSelect(option), className: `flex items-center px-3 py-2 cursor-pointer text-sm ${isSelected ? 'bg-primary-500 text-white' : 'text-neutral-900 hover:bg-neutral-200'}`, children: option.name }, option.key));
                            }), filteredOptions.length === 0 && (_jsx("div", { className: 'px-3 py-2', children: _jsx(Text, { size: 'sm', className: 'text-neutral-400', children: searchTerm.trim()
                                        ? translate('No matching options')
                                        : emptyOptionsMessage || translate('No options available') }) }))] })] }))] }));
};
